import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box, HStack, Image, Input, InputGroup, InputLeftElement, InputRightElement,
  Portal, Spinner, Stack, Text,
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { catalogCoverImage } from '../catalog/assets';
import { searchSongs, CatalogSong } from '../catalog/api';

export interface ReferenceLike {
  title: string;
  artist: string;
  slug?: string;
  artistSlug?: string;
  cover?: string;
  styleNotes?: string;
}

/**
 * Backend-powered song picker for the generator. Debounced text search
 * (300 ms) that hits the mixgenie-service public song-catalog endpoint —
 * the same catalog that DiceLaCancion browses, so we get real cover art
 * + slug + iTunes preview URL for anything Mureka can be seeded with.
 *
 * Two result sources merged into one list:
 *   • `results` — songs already indexed in Mongo (returns instantly)
 *   • `external` — Genius / iTunes hits fetched live on page 1
 * We show `external` behind an "importing…" hint the moment the user
 * picks one; the backend two-phase-imports it and we treat the resulting
 * artist/slug pair as the canonical reference.
 */
export function ReferenceSearch({ onPick }: { onPick: (song: ReferenceLike) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<CatalogSong[]>([]);
  const [external, setExternal] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  // Dropdown is rendered via a Portal so it escapes the parent
  // FieldBlock's backdrop-filter stacking context (which was hiding it
  // behind the next FieldBlock). Portal → we position by tracking the
  // input's bounding rect and updating on scroll / resize.
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const el = boxRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.bottom + 6, left: r.left, width: r.width });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, q, results.length, external.length]);

  useEffect(() => {
    const trimmed = q.trim();
    if (!trimmed || trimmed.length < 2) {
      setResults([]);
      setExternal([]);
      return;
    }
    setLoading(true);
    const handle = window.setTimeout(async () => {
      try {
        const res = await searchSongs(trimmed, 1, 8);
        setResults(res.results || []);
        setExternal(res.external || []);
      } catch { /* silent — no dropdown, user retries */ }
      finally { setLoading(false); }
    }, 300);
    return () => window.clearTimeout(handle);
  }, [q]);

  // Close dropdown when user clicks elsewhere.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, []);

  const pick = (song: any, isExternal: boolean) => {
    const styleNotes = [song.songTags, song.albumName].filter(Boolean).join(' · ').slice(0, 200);
    const thumb =
      song.coverImage ||
      song.song_thumbnail_image ||
      song.media?.songThumbnailImage ||
      song.thumbnail ||
      song.artworkUrl ||
      undefined;
    onPick({
      title: song.songTitle || song.title || '',
      artist: song.artistName || song.artist || '',
      slug: !isExternal ? song.slug : undefined,
      artistSlug: !isExternal ? song.artistSlug : undefined,
      cover: thumb,
      styleNotes,
    });
    setQ('');
    setResults([]);
    setExternal([]);
    setOpen(false);
  };

  const showList = open && q.trim().length >= 2;
  const hasAny = results.length > 0 || external.length > 0;

  return (
    <Box position="relative" ref={boxRef}>
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="whiteAlpha.500" />
        </InputLeftElement>
        <Input
          value={q}
          placeholder={t('generator.refSearchPlaceholder', 'Search a song to base yours on…')}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          bg="rgba(10, 6, 22, 0.55)"
          border="1px solid"
          borderColor="whiteAlpha.200"
          color="whiteAlpha.900"
          borderRadius="12px"
          _hover={{ borderColor: 'whiteAlpha.300' }}
          _focus={{ borderColor: 'pink.300', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)', bg: 'rgba(10, 6, 22, 0.75)' }}
          _placeholder={{ color: 'whiteAlpha.400' }}
        />
        {loading && (
          <InputRightElement>
            <Spinner size="xs" color="pink.300" />
          </InputRightElement>
        )}
        {!loading && q && (
          <InputRightElement>
            <Box
              as="button"
              onClick={() => { setQ(''); setResults([]); setExternal([]); }}
              color="whiteAlpha.500"
              _hover={{ color: 'whiteAlpha.900' }}
              aria-label="Clear search"
            >
              <CloseIcon boxSize={2.5} />
            </Box>
          </InputRightElement>
        )}
      </InputGroup>

      {showList && rect && (
        <Portal>
        <Box
          position="fixed"
          top={`${rect.top}px`}
          left={`${rect.left}px`}
          w={`${rect.width}px`}
          bg="rgba(15, 11, 31, 0.98)"
          border="1px solid"
          borderColor="whiteAlpha.200"
          borderRadius="14px"
          boxShadow="0 20px 48px rgba(0,0,0,0.55)"
          maxH="360px"
          overflowY="auto"
          zIndex={2000}
        >
          {loading && !hasAny && (
            <HStack p={4} justify="center"><Spinner size="sm" color="pink.300" /></HStack>
          )}
          {!loading && !hasAny && (
            <Text p={4} fontSize="sm" color="whiteAlpha.600" textAlign="center">
              {t('generator.refSearchEmpty', 'No matches. Try a different title or artist.')}
            </Text>
          )}
          {results.length > 0 && (
            <Stack spacing={0}>
              {results.map((r) => (
                <ResultRow
                  key={String(r._id) || `${r.artistSlug}/${r.slug}`}
                  cover={r.media?.songThumbnailImage}
                  title={r.songTitle}
                  artist={r.artistName}
                  badge={undefined}
                  onClick={() => pick(r, false)}
                />
              ))}
            </Stack>
          )}
          {external.length > 0 && (
            <>
              <Box
                px={3} py={2}
                bg="rgba(255,255,255,0.03)"
                borderTop="1px solid"
                borderColor="whiteAlpha.100"
              >
                <Text
                  fontSize="10px"
                  color="whiteAlpha.500"
                  fontFamily="ui-monospace, SF Mono, Menlo, monospace"
                  letterSpacing="0.1em"
                  textTransform="uppercase"
                >
                  {t('generator.refSearchExternal', 'From Genius / iTunes — imported on pick')}
                </Text>
              </Box>
              <Stack spacing={0}>
                {external.slice(0, 5).map((e) => (
                  <ResultRow
                    key={`${e.provider}-${e.externalId}`}
                    cover={e.thumbnail || e.artworkUrl}
                    title={e.songTitle || e.title}
                    artist={e.artistName || e.artist}
                    badge={e.provider}
                    onClick={() => pick(e, true)}
                  />
                ))}
              </Stack>
            </>
          )}
        </Box>
        </Portal>
      )}
    </Box>
  );
}

function ResultRow({
  cover, title, artist, badge, onClick,
}: {
  cover?: string; title: string; artist: string; badge?: string; onClick: () => void;
}) {
  return (
    <HStack
      as="button"
      onClick={onClick}
      w="full"
      spacing={3}
      p={3}
      textAlign="left"
      _hover={{ bg: 'whiteAlpha.100' }}
      _focus={{ bg: 'whiteAlpha.150', outline: 'none' }}
      transition="background 0.1s"
    >
      <Image
        src={catalogCoverImage(cover, 'song')}
        alt=""
        boxSize="42px"
        borderRadius="8px"
        objectFit="cover"
        flexShrink={0}
      />
      <Stack spacing={0} flex="1" minW={0}>
        <Text fontSize="sm" fontWeight={700} color="whiteAlpha.900" noOfLines={1}>
          {title || '—'}
        </Text>
        <Text fontSize="xs" color="whiteAlpha.600" noOfLines={1}>
          {artist || '—'}
        </Text>
      </Stack>
      {badge && (
        <Text
          fontSize="9px"
          fontFamily="ui-monospace, SF Mono, Menlo, monospace"
          color="whiteAlpha.500"
          textTransform="uppercase"
          letterSpacing="0.1em"
          flexShrink={0}
        >
          {badge}
        </Text>
      )}
    </HStack>
  );
}
